import { useCallback, useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { Trash2, X, Eye } from 'lucide-react'
import { adminApi, unwrap } from '../../services/api'
import { useModalA11y } from '../../hooks/useModalA11y'
import LoadErrorState from '../common/LoadErrorState'
import {
  SERVER_ERROR_MESSAGE,
  SERVER_TIMEOUT_MESSAGE,
  withServerTimeout,
} from '../common/loadErrorUtils'
import { useColors } from '../../hooks/useColors'

type DiscountType = 'percent' | 'flat'

interface Coupon {
  id: string
  code: string
  discount_type: DiscountType
  discount_value: number | string
  min_order?: number | string
  max_discount?: number | string | null
  used_count?: number | string
  usage_limit?: number | string | null
  per_customer_limit?: number | string | null
  expires_at?: string | null
  is_active?: boolean
  show_in_app?: boolean
}

interface CouponPayload {
  data?: Coupon[]
}

interface Redemption {
  id: string
  customer_name: string
  customer_phone: string
  order_display_id: string
  order_number: string
  discount_amount: number | string
  redeemed_at: string
  payment_status: string
}

const randomCode = () => Math.random().toString(36).slice(2, 10).toUpperCase()

const todayIso = () => new Date().toLocaleDateString('en-CA')

const formatDate = (value?: string | null) => {
  if (!value) {
    return 'No expiry'
  }
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })
}

const formatDateTime = (value: string) =>
  new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

const getDerivedStatus = (coupon: Coupon): 'Active' | 'Expired' | 'Disabled' => {
  if (!coupon.is_active) {
    return 'Disabled'
  }

  if (coupon.expires_at && new Date(coupon.expires_at).getTime() < Date.now()) {
    return 'Expired'
  }

  return 'Active'
}

const PER_CUSTOMER_OPTIONS = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '0', label: 'Unlimited' },
]

export default function CouponsTab() {
  const colors = useColors()
  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percent' as DiscountType,
    discount_value: '',
    min_order: '',
    max_discount: '',
    usage_limit: '',
    per_customer_limit: '1',
    expires_at: '',
    is_active: true,
    show_in_app: true,
  })

  // Redemption viewer state
  const [redemptionCoupon, setRedemptionCoupon] = useState<Coupon | null>(null)
  const [redemptions, setRedemptions] = useState<Redemption[]>([])
  const [redemptionsLoading, setRedemptionsLoading] = useState(false)

  const emptyForm = {
    code: '',
    discount_type: 'percent' as DiscountType,
    discount_value: '',
    min_order: '',
    max_discount: '',
    usage_limit: '',
    per_customer_limit: '1',
    expires_at: '',
    is_active: true,
    show_in_app: true,
  }

  const closeModal = () => {
    setShowCreateModal(false)
    setEditingId(null)
    setFormData(emptyForm)
    setError('')
  }

  const modalRef = useModalA11y<HTMLFormElement>(showCreateModal, closeModal)

  const fetchCoupons = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const response = await withServerTimeout(adminApi.getCoupons())
      const payload = unwrap<CouponPayload | Coupon[]>(response)

      if (Array.isArray(payload)) {
        setCoupons(payload)
        return
      }

      setCoupons(Array.isArray(payload?.data) ? payload.data : [])
    } catch (loadError) {
      setError(loadError instanceof Error && loadError.message === SERVER_TIMEOUT_MESSAGE ? SERVER_TIMEOUT_MESSAGE : SERVER_ERROR_MESSAGE)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchCoupons()
  }, [fetchCoupons])

  const activeCount = useMemo(
    () => coupons.filter((coupon) => coupon.is_active === true).length,
    [coupons],
  )

  const handleSubmitCoupon = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const discountValue = Number(formData.discount_value || 0)
    const minOrder = Number(formData.min_order || 0)
    const maxDiscount = formData.max_discount ? Number(formData.max_discount) : null
    const usageLimit = formData.usage_limit ? Number(formData.usage_limit) : null
    const perCustomerLimit = Number(formData.per_customer_limit || 1)

    if (!Number.isFinite(discountValue) || discountValue < 0) {
      setError('Discount value cannot be negative.')
      return
    }
    if (formData.discount_type === 'percent' && discountValue > 100) {
      setError('Percentage discount cannot exceed 100%.')
      return
    }
    if (!Number.isFinite(minOrder) || minOrder < 0) {
      setError('Minimum order cannot be negative.')
      return
    }
    if (maxDiscount !== null && (!Number.isFinite(maxDiscount) || maxDiscount < 0)) {
      setError('Max discount cannot be negative.')
      return
    }
    if (maxDiscount !== null && formData.discount_type === 'flat') {
      setError('Max discount only applies to percentage coupons. Remove it for flat coupons.')
      return
    }
    if (usageLimit !== null && (!Number.isInteger(usageLimit) || usageLimit <= 0)) {
      setError('Usage limit must be a positive whole number.')
      return
    }
    if (formData.expires_at && formData.expires_at < todayIso()) {
      setError('Expiry date cannot be in the past.')
      return
    }

    setSaving(true)
    setError('')

    const body = {
      code: formData.code.toUpperCase(),
      discount_type: formData.discount_type,
      discount_value: discountValue,
      min_order: minOrder,
      max_discount: maxDiscount,
      usage_limit: usageLimit,
      per_customer_limit: perCustomerLimit,
      expires_at: formData.expires_at || null,
      is_active: formData.is_active,
      show_in_app: formData.show_in_app,
    }

    try {
      if (editingId) {
        await adminApi.updateCoupon(editingId, body)
      } else {
        await adminApi.createCoupon(body)
      }
      closeModal()
      await fetchCoupons()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to save coupon. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleEditCoupon = (coupon: Coupon) => {
    setEditingId(coupon.id)
    const pcl = coupon.per_customer_limit
    setFormData({
      code: coupon.code,
      discount_type: coupon.discount_type,
      discount_value: String(coupon.discount_value ?? ''),
      min_order: String(coupon.min_order ?? ''),
      max_discount: coupon.max_discount === null || coupon.max_discount === undefined ? '' : String(coupon.max_discount),
      usage_limit: coupon.usage_limit === null || coupon.usage_limit === undefined ? '' : String(coupon.usage_limit),
      per_customer_limit: pcl === null || pcl === undefined ? '1' : String(pcl),
      expires_at: coupon.expires_at ? new Date(coupon.expires_at).toISOString().slice(0, 10) : '',
      is_active: coupon.is_active !== false,
      show_in_app: coupon.show_in_app !== false,
    })
    setShowCreateModal(true)
  }

  const handleDeleteCoupon = async (coupon: Coupon) => {
    if (!window.confirm(`Delete coupon "${coupon.code}"? This cannot be undone.`)) {
      return
    }
    setDeletingId(coupon.id)
    setError('')
    try {
      await adminApi.deleteCoupon(coupon.id)
      await fetchCoupons()
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Unable to delete coupon. Please try again.')
    } finally {
      setDeletingId(null)
    }
  }

  const handleViewRedemptions = async (coupon: Coupon) => {
    setRedemptionCoupon(coupon)
    setRedemptionsLoading(true)
    try {
      const result = await adminApi.getCouponRedemptions(coupon.id, 1, 50)
      const payload = result?.data?.data ?? result?.data ?? []
      setRedemptions(Array.isArray(payload) ? payload : [])
    } catch {
      setRedemptions([])
    } finally {
      setRedemptionsLoading(false)
    }
  }

  const perCustomerLabel = (coupon: Coupon) => {
    const v = Number(coupon.per_customer_limit ?? 1)
    return v === 0 ? '∞' : String(v)
  }

  return (
    <div>
      <div style={{ margin: '20px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 className="font-display" style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: colors.textPrimary }}>
          Active Coupons: {activeCount}
        </h2>

        <button
          type="button"
          className="font-body"
          onClick={() => {
            setEditingId(null)
            setFormData(emptyForm)
            setError('')
            setShowCreateModal(true)
          }}
          style={{
            border: 'none',
            background: '#D6B97B',
            color: colors.textPrimary,
            fontSize: '14px',
            fontWeight: 600,
            padding: '10px 20px',
            borderRadius: '10px',
            cursor: 'pointer',
          }}
        >
          + Create Coupon
        </button>
      </div>

      <div
        style={{
          background: colors.bgCard,
          borderRadius: '16px',
          margin: '0 32px 32px',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
          overflow: 'auto',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: colors.bgSecondary, borderBottom: `1px solid ${colors.border}` }}>
              {['Code', 'Type', 'Value', 'Min Order', 'Uses', 'Per Customer', 'Expiry', 'Status', 'Visibility', 'Actions'].map((header) => (
                <th key={header} className="font-body" style={{ padding: '16px 16px', fontSize: '11px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {error && !loading ? (
              <tr>
                <td colSpan={10}>
                  <LoadErrorState message={error} onRetry={() => void fetchCoupons()} margin="24px" />
                </td>
              </tr>
            ) : loading ? (
              <tr>
                <td colSpan={10} style={{ padding: '32px 20px', textAlign: 'center' }}>
                  <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textMuted }}>
                    Loading coupons...
                  </p>
                </td>
              </tr>
            ) : coupons.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ padding: '32px 20px', textAlign: 'center' }}>
                  <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textMuted }}>
                    No coupons found.
                  </p>
                </td>
              </tr>
            ) : (
              coupons.map((coupon) => {
              const status = getDerivedStatus(coupon)

              return (
                <tr key={coupon.id} style={{ borderBottom: `0.5px solid ${colors.surface}` }}>
                  <td style={{ padding: '16px', fontFamily: 'Courier New, monospace', fontSize: '14px', fontWeight: 700, color: colors.textPrimary }}>
                    {coupon.code}
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span className="font-body" style={{ background: colors.surface, color: colors.textPrimary, borderRadius: '999px', padding: '4px 10px', fontSize: '12px', fontWeight: 500 }}>
                      {coupon.discount_type === 'percent' ? 'Percentage' : 'Flat'}
                    </span>
                  </td>
                  <td className="font-body" style={{ padding: '16px', fontSize: '14px', fontWeight: 600, color: '#D6B97B' }}>
                    {coupon.discount_type === 'percent' ? `${Number(coupon.discount_value || 0)}%` : `₹${Number(coupon.discount_value || 0)}`}
                    {coupon.discount_type === 'percent' && coupon.max_discount !== null && coupon.max_discount !== undefined && coupon.max_discount !== '' ? (
                      <span style={{ display: 'block', fontSize: '11px', fontWeight: 400, color: colors.textMuted }}>
                        max ₹{Number(coupon.max_discount)}
                      </span>
                    ) : null}
                  </td>
                  <td className="font-body" style={{ padding: '16px', fontSize: '13px', fontWeight: 400, color: colors.textSecondary }}>
                    ₹{Number(coupon.min_order || 0)}
                  </td>
                  <td className="font-body" style={{ padding: '16px', fontSize: '13px', fontWeight: 400, color: colors.textSecondary }}>
                    {Number(coupon.used_count || 0)} / {coupon.usage_limit === null || coupon.usage_limit === undefined ? '∞' : Number(coupon.usage_limit)}
                  </td>
                  <td className="font-body" style={{ padding: '16px', fontSize: '13px', fontWeight: 400, color: colors.textSecondary }}>
                    {perCustomerLabel(coupon)}
                  </td>
                  <td className="font-body" style={{ padding: '16px', fontSize: '13px', fontWeight: 400, color: colors.textSecondary }}>
                    {formatDate(coupon.expires_at)}
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span className="font-body" style={{ borderRadius: '999px', padding: '4px 10px', fontSize: '11px', fontWeight: 500, background: status === 'Active' ? '#F0FDF4' : status === 'Expired' ? '#FEF2F2' : colors.bgSecondary, color: status === 'Active' ? '#15803D' : status === 'Expired' ? '#991B1B' : colors.textMuted, border: `1px solid ${status === 'Active' ? '#86EFAC' : status === 'Expired' ? '#FCA5A5' : colors.border}` }}>
                      {status}
                    </span>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span className="font-body" style={{ borderRadius: '999px', padding: '4px 10px', fontSize: '11px', fontWeight: 500, background: coupon.show_in_app !== false ? '#F0FDF4' : '#FEF9C3', color: coupon.show_in_app !== false ? '#15803D' : '#92400E', border: `1px solid ${coupon.show_in_app !== false ? '#86EFAC' : '#FDE68A'}` }}>
                      {coupon.show_in_app !== false ? 'Public' : 'Hidden'}
                    </span>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      <button type="button" className="font-body" onClick={() => handleEditCoupon(coupon)} style={{ border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '4px 10px', fontSize: '12px', fontWeight: 600, color: colors.textPrimary, background: 'transparent', cursor: 'pointer' }}>
                        Edit
                      </button>
                      <button type="button" aria-label={`View redemptions for ${coupon.code}`} onClick={() => void handleViewRedemptions(coupon)} style={{ border: `1px solid ${colors.border}`, borderRadius: '6px', background: 'transparent', cursor: 'pointer', padding: '4px', display: 'grid', placeItems: 'center' }}>
                        <Eye size={15} color="#D6B97B" />
                      </button>
                      <button type="button" aria-label={`Delete coupon ${coupon.code}`} onClick={() => void handleDeleteCoupon(coupon)} disabled={deletingId === coupon.id} style={{ border: `1px solid ${colors.border}`, borderRadius: '6px', background: 'transparent', cursor: deletingId === coupon.id ? 'not-allowed' : 'pointer', padding: '4px', display: 'grid', placeItems: 'center', opacity: deletingId === coupon.id ? 0.5 : 1 }}>
                        <Trash2 size={15} color="#B91C1C" />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            }))}
          </tbody>
        </table>
      </div>

      {/* Create / Edit Modal */}
      {showCreateModal ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 70,
            background: 'rgba(0,0,0,0.40)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
          onClick={closeModal}
        >
          <form
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            onSubmit={(event) => {
              void handleSubmitCoupon(event)
            }}
            onClick={(event) => event.stopPropagation()}
            style={{
              width: '480px',
              maxHeight: '90vh',
              overflowY: 'auto',
              background: colors.bgCard,
              borderRadius: '20px',
              boxShadow: '0px 24px 64px rgba(15,46,42,0.20)',
              padding: '32px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 className="font-display" style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: colors.textPrimary }}>
                {editingId ? 'Edit Coupon' : 'Create Coupon Code'}
              </h3>
              <button type="button" onClick={closeModal} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}>
                <X size={20} color={colors.textPrimary} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <label className="font-body" style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                    Coupon Code
                  </label>
                  <button type="button" className="font-body" onClick={() => setFormData((prev) => ({ ...prev, code: randomCode() }))} style={{ border: 'none', background: 'transparent', color: '#D6B97B', cursor: 'pointer', fontSize: '13px', fontWeight: 600, padding: 0 }}>
                    Generate random
                  </button>
                </div>
                <input
                  required
                  value={formData.code}
                  onChange={(event) => setFormData((prev) => ({ ...prev, code: event.target.value.toUpperCase() }))}
                  className="font-body"
                  style={{ width: '100%', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '12px 16px', fontFamily: 'Courier New, monospace', fontSize: '14px', fontWeight: 700, textTransform: 'uppercase' }}
                />
              </div>

              <div>
                <p className="font-body" style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                  Discount Type
                </p>
                <div style={{ display: 'flex', gap: '12px' }}>
                  {[
                    { value: 'percent', label: 'Percentage %' },
                    { value: 'flat', label: 'Flat Amount ₹' },
                  ].map((option) => {
                    const isSelected = formData.discount_type === option.value
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className="font-body"
                        onClick={() => setFormData((prev) => ({ ...prev, discount_type: option.value as DiscountType }))}
                        style={{
                          flex: 1,
                          borderRadius: '999px',
                          border: `1.5px solid ${isSelected ? '#D6B97B' : colors.border}`,
                          background: isSelected ? '#D6B97B' : 'transparent',
                          color: isSelected ? colors.textPrimary : colors.textSecondary,
                          fontSize: '14px',
                          fontWeight: 600,
                          padding: '10px 14px',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(event) => {
                          if (!isSelected) {
                            event.currentTarget.style.background = 'rgba(214,185,123,0.08)'
                          }
                        }}
                        onMouseLeave={(event) => {
                          if (!isSelected) {
                            event.currentTarget.style.background = 'transparent'
                          }
                        }}
                      >
                        {option.label}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <input required type="number" min="0" max={formData.discount_type === 'percent' ? '100' : undefined} step="0.01" placeholder="Discount Value" value={formData.discount_value} onChange={(event) => setFormData((prev) => ({ ...prev, discount_value: event.target.value }))} className="font-body" style={{ flex: 1, padding: '12px 16px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', fontSize: '14px' }} />
                <input required type="number" min="0" step="0.01" placeholder="Min Order (₹)" value={formData.min_order} onChange={(event) => setFormData((prev) => ({ ...prev, min_order: event.target.value }))} className="font-body" style={{ flex: 1, padding: '12px 16px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', fontSize: '14px' }} />
              </div>

              {formData.discount_type === 'percent' ? (
                <div>
                  <label className="font-body" style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                    Max Discount (₹)
                  </label>
                  <input type="number" min="0" step="0.01" placeholder="No cap (e.g. 300)" value={formData.max_discount} onChange={(event) => setFormData((prev) => ({ ...prev, max_discount: event.target.value }))} className="font-body" style={{ width: '100%', padding: '12px 16px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', fontSize: '14px' }} />
                  <p className="font-body" style={{ margin: '6px 0 0', fontSize: '12px', color: colors.textMuted }}>
                    Caps the discount on large orders. Leave blank for no cap. E.g. 60% with a ₹300 cap = max ₹300 off.
                  </p>
                </div>
              ) : null}

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label className="font-body" style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                    Total Usage Limit
                  </label>
                  <input type="number" min="1" step="1" placeholder="Unlimited" value={formData.usage_limit} onChange={(event) => setFormData((prev) => ({ ...prev, usage_limit: event.target.value }))} className="font-body" style={{ width: '100%', padding: '12px 16px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', fontSize: '14px' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="font-body" style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                    Per Customer Limit
                  </label>
                  <select
                    value={formData.per_customer_limit}
                    onChange={(event) => setFormData((prev) => ({ ...prev, per_customer_limit: event.target.value }))}
                    className="font-body"
                    style={{ width: '100%', padding: '12px 16px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', fontSize: '14px', background: colors.bgCard }}
                  >
                    {PER_CUSTOMER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="font-body" style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                  Expiry Date
                </label>
                <input type="date" min={todayIso()} value={formData.expires_at} onChange={(event) => setFormData((prev) => ({ ...prev, expires_at: event.target.value }))} className="font-body" style={{ width: '100%', padding: '12px 16px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', fontSize: '14px' }} />
              </div>

              <label className="font-body" style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', fontWeight: 600, color: colors.textPrimary, cursor: 'pointer' }}>
                <input type="checkbox" checked={formData.is_active} onChange={(event) => setFormData((prev) => ({ ...prev, is_active: event.target.checked }))} />
                Active (customers can use this coupon)
              </label>

              <label className="font-body" style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', fontWeight: 600, color: colors.textPrimary, cursor: 'pointer' }}>
                <input type="checkbox" checked={formData.show_in_app} onChange={(event) => setFormData((prev) => ({ ...prev, show_in_app: event.target.checked }))} />
                Show in Customer App
              </label>
              {!formData.show_in_app ? (
                <p className="font-body" style={{ margin: '-12px 0 0 28px', fontSize: '12px', color: colors.textMuted }}>
                  Hidden coupons won&apos;t appear in Available Offers, but customers can still apply them by entering the code manually.
                </p>
              ) : null}

              {error ? (
                <p className="font-body" style={{ margin: 0, fontSize: '13px', color: '#B91C1C' }}>{error}</p>
              ) : null}

              <button type="submit" disabled={saving} className="font-body" style={{ width: '100%', border: 'none', borderRadius: '12px', background: '#D6B97B', color: colors.textPrimary, fontSize: '15px', fontWeight: 600, padding: '14px', marginTop: '8px', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Coupon'}
              </button>

              <button type="button" onClick={closeModal} className="font-body" style={{ width: '100%', border: `1.5px solid ${colors.border}`, borderRadius: '12px', background: 'transparent', color: colors.textSecondary, fontSize: '14px', fontWeight: 600, padding: '12px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {/* Redemption History Modal */}
      {redemptionCoupon ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 70,
            background: 'rgba(0,0,0,0.40)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
          onClick={() => setRedemptionCoupon(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
            style={{
              width: '640px',
              maxHeight: '80vh',
              overflowY: 'auto',
              background: colors.bgCard,
              borderRadius: '20px',
              boxShadow: '0px 24px 64px rgba(15,46,42,0.20)',
              padding: '32px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 className="font-display" style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>
                Redemptions · <span style={{ fontFamily: 'Courier New, monospace' }}>{redemptionCoupon.code}</span>
              </h3>
              <button type="button" onClick={() => setRedemptionCoupon(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}>
                <X size={20} color={colors.textPrimary} />
              </button>
            </div>

            {redemptionsLoading ? (
              <p className="font-body" style={{ textAlign: 'center', fontSize: '14px', color: colors.textMuted }}>Loading…</p>
            ) : redemptions.length === 0 ? (
              <p className="font-body" style={{ textAlign: 'center', fontSize: '14px', color: colors.textMuted }}>No redemptions yet.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                    {['Customer', 'Phone', 'Order', 'Discount', 'Date', 'Payment'].map((h) => (
                      <th key={h} className="font-body" style={{ padding: '10px 8px', fontSize: '11px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', textAlign: 'left' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {redemptions.map((r) => (
                    <tr key={r.id} style={{ borderBottom: `0.5px solid ${colors.surface}` }}>
                      <td className="font-body" style={{ padding: '10px 8px', fontSize: '13px', color: colors.textPrimary }}>{r.customer_name || '-'}</td>
                      <td className="font-body" style={{ padding: '10px 8px', fontSize: '13px', color: colors.textSecondary }}>{r.customer_phone || '-'}</td>
                      <td className="font-body" style={{ padding: '10px 8px', fontSize: '13px', fontFamily: 'Courier New, monospace', color: colors.textPrimary }}>{r.order_display_id || r.order_number}</td>
                      <td className="font-body" style={{ padding: '10px 8px', fontSize: '13px', fontWeight: 600, color: '#D6B97B' }}>₹{Number(r.discount_amount || 0)}</td>
                      <td className="font-body" style={{ padding: '10px 8px', fontSize: '12px', color: colors.textSecondary }}>{formatDateTime(r.redeemed_at)}</td>
                      <td style={{ padding: '10px 8px' }}>
                        <span className="font-body" style={{
                          borderRadius: '999px',
                          padding: '3px 8px',
                          fontSize: '11px',
                          fontWeight: 500,
                          background: r.payment_status === 'paid' ? '#F0FDF4' : r.payment_status === 'failed' ? '#FEF2F2' : colors.bgSecondary,
                          color: r.payment_status === 'paid' ? '#15803D' : r.payment_status === 'failed' ? '#991B1B' : colors.textMuted,
                          border: `1px solid ${r.payment_status === 'paid' ? '#86EFAC' : r.payment_status === 'failed' ? '#FCA5A5' : colors.border}`,
                        }}>
                          {r.payment_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
